import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { getAuthLink } from '@/lib/links';

const DEPARTMENTS = [
  {
    id: 'reception',
    name: '24/7 AI Reception',
    headlinePrefix: 'Every phone call answered in two rings.',
    headlineAccent: 'Done.',
    description:
      'Ashley answers homeowner inquiries at 2 PM or 2 AM, qualifies roof damage, checks your Google Calendar, and books on-site inspections.',
    cta: 'Get Started',
    dashboardImage: '/product-frames/ai-agents.webp',
    dashboardAlt: 'Rooflyzer AI Agents workspace showing office lines and call recordings',
    secondaryImage: '/product-frames/ai-recordings.webp',
    secondaryAlt: 'Rooflyzer Recordings showing call audio waveform and transcript',
    primaryLabel: 'AI Office Lines',
    secondaryLabel: 'Call Recordings',
  },
  {
    id: 'takeoffs',
    name: 'Satellite Takeoffs',
    headlinePrefix: 'Instant 3D measurements from high-res satellite.',
    headlineAccent: 'Done.',
    description:
      'Tyler pulls satellite aerial imagery, traces perimeter eave and ridge lines, detects pitch, and outputs accurate squares in under 90 seconds.',
    cta: 'Get Started',
    dashboardImage: '/product-frames/estimates.webp',
    dashboardAlt: 'Rooflyzer Measured Roof Estimate showing $14,993.23 total and edge breakdown',
    secondaryImage: '/product-frames/roof-3d.webp',
    secondaryAlt: 'Rooflyzer 3D Roof Viewer showing 6/12 hip roof model',
    primaryLabel: 'Measured Estimate ($14,993.23)',
    secondaryLabel: '3D Roof Viewer',
  },
  {
    id: 'storm',
    name: 'Storm Response',
    headlinePrefix: 'Hail path swath to homeowner door in 14 minutes.',
    headlineAccent: 'Done.',
    description:
      'Marcus tracks NOAA Doppler Level-II radar swaths in real-time, lines up affected properties, and coordinates repair crews street-by-street.',
    cta: 'Get Started',
    dashboardImage: '/product-frames/leads.webp',
    dashboardAlt: 'Rooflyzer Leads workspace showing $208,600 pipeline value',
    secondaryImage: '/product-frames/pipeline.webp',
    secondaryAlt: 'Rooflyzer Sales Pipeline 4-stage board',
    primaryLabel: 'Leads Workspace',
    secondaryLabel: 'Sales Pipeline',
  },
  {
    id: 'speed-to-lead',
    name: 'Speed-to-Lead CRM',
    headlinePrefix: 'Lead response in 38 seconds. No lost bids.',
    headlineAccent: 'Done.',
    description:
      'Megan texts homeowners before your competitors even see the lead notification, locking down qualified repair appointments before they look elsewhere.',
    cta: 'Get Started',
    dashboardImage: '/product-frames/lead-detail.webp',
    dashboardAlt: 'Rooflyzer Lead Detail for Marcus Whitfield at 4821 Ridgemont Drive, Plano TX',
    secondaryImage: '/product-frames/leads.webp',
    secondaryAlt: 'Rooflyzer Leads table showing intent scores and stages',
    primaryLabel: 'Lead Detail (Marcus Whitfield)',
    secondaryLabel: 'All Leads ($208,600)',
  },
  {
    id: 'escrow',
    name: 'Milestone Escrow',
    headlinePrefix: 'Cash flow secured before shingles hit the driveway.',
    headlineAccent: 'Done.',
    description:
      'Sofia splits roof jobs into protected escrow milestones (Deposit, Tear-off, Final Walkthrough) with automated contractor release draws.',
    cta: 'Get Started',
    dashboardImage: '/product-frames/invoices.webp',
    dashboardAlt: 'Rooflyzer Invoices table showing IN-20841-1 Paid $13,450',
    secondaryImage: '/product-frames/reports.webp',
    secondaryAlt: 'Rooflyzer Finance hub showing Invoice Revenue Trend',
    primaryLabel: 'Invoices Ledger',
    secondaryLabel: 'Finance Overview',
  },
];

export function DepartmentWorkOSMonday() {
  const [activeDeptIndex, setActiveDeptIndex] = useState(0);
  const [showSecondaryView, setShowSecondaryView] = useState(false);
  const [pitchSlider, setPitchSlider] = useState(6);

  const currentDept = DEPARTMENTS[activeDeptIndex];
  const calculatedSquares = (26.8 * (1 + (pitchSlider - 4) * 0.05)).toFixed(1);

  const handleSelectDepartment = (index: number) => {
    setActiveDeptIndex(index);
    setShowSecondaryView(false);
  };

  return (
    <section className="py-20 sm:py-24 bg-white">
      <div className="max-w-[1380px] mx-auto px-5 sm:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-4xl sm:text-[48px] lg:text-[56px] font-normal tracking-[-0.03em] text-[#000000] leading-[1.12]">
            Get more done with agents
          </h2>
        </div>

        {/* Clean Department Tab Bar */}
        <div className="flex justify-center mb-12 overflow-x-auto pb-2 scrollbar-none">
          <div
            className="inline-flex items-center gap-1 p-1.5 rounded-xl bg-[#F5F5F2] border border-gray-200 max-w-full"
            role="tablist"
            aria-label="Department boards"
          >
            {DEPARTMENTS.map((dept, idx) => {
              const isActive = activeDeptIndex === idx;
              return (
                <button
                  key={dept.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => handleSelectDepartment(idx)}
                  className={`relative px-4 sm:px-5 py-2 rounded-lg text-xs sm:text-[14px] transition-colors whitespace-nowrap ${
                    isActive
                      ? 'bg-[#0B1120] text-white font-medium'
                      : 'text-[#323338] font-normal hover:text-[#000000]'
                  }`}
                >
                  <span>{dept.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Presentation Grid: Left Editorial Context + Right Full-Size Real Web-App Screen */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          {/* Left Column (4 cols) */}
          <div className="lg:col-span-4 flex flex-col justify-between pr-0 lg:pr-4">
            <motion.div
              key={currentDept.id}
              initial={{ opacity: 0.85, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              <h3 className="text-2xl sm:text-[32px] font-normal text-[#000000] tracking-[-0.02em] leading-[1.22] mb-5">
                {currentDept.headlinePrefix}{' '}
                <span className="font-semibold text-[#0B1120]">{currentDept.headlineAccent}</span>
              </h3>

              <p className="text-sm sm:text-[16px] text-[#323338] leading-[1.6] font-normal mb-7">
                {currentDept.description}
              </p>

              {/* Satellite Takeoffs Pitch Calculator with Real 3D Roof Viewer */}
              {currentDept.id === 'takeoffs' && (
                <div className="mb-7 p-4 rounded-xl bg-[#F5F5F2] border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-[#161616]">
                      2,683 sq ft · 6 facets
                    </span>
                    <span className="text-xs font-medium text-[#676879]">3D Facet Mesh</span>
                  </div>

                  <div className="flex justify-between items-center text-xs font-medium text-[#161616] mb-1.5">
                    <span>Roof Pitch:</span>
                    <span className="font-semibold text-[#0B1120]">{pitchSlider}/12</span>
                  </div>
                  <input
                    type="range"
                    min="4"
                    max="12"
                    value={pitchSlider}
                    onChange={(e) => setPitchSlider(Number(e.target.value))}
                    aria-label="Pitch adjustment"
                    className="w-full accent-[#0B1120] cursor-pointer"
                  />
                  <div className="mt-1.5 text-xs text-[#676879] flex justify-between">
                    <span>Squares with waste:</span>
                    <span className="font-semibold text-[#161616]">{calculatedSquares} SQ</span>
                  </div>
                </div>
              )}
            </motion.div>

            <div>
              <a
                href={getAuthLink('/register')}
                className="inline-flex min-h-12 items-center gap-2.5 px-7 py-3 rounded-lg font-semibold text-[15px] bg-[#FBC72D] text-[#161616] hover:bg-[#e5b320] transition-all shadow-xs"
              >
                <span>{currentDept.cta}</span>
                <ArrowRight className="size-4" />
              </a>
            </div>
          </div>

          {/* Right Column (8 cols): 100% Real Unobstructed Rooflyzer Web-App Screen */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-[0_16px_48px_rgba(11,17,32,0.08)] overflow-hidden">
              <div className="px-4 py-2.5 border-b border-gray-200 flex items-center justify-between gap-2 bg-[#FAFAFC]">
                <span className="font-semibold text-xs sm:text-sm text-[#161616]">
                  Rooflyzer OS · {currentDept.name}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    aria-pressed={!showSecondaryView}
                    onClick={() => setShowSecondaryView(false)}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      !showSecondaryView
                        ? 'bg-[#0B1120] text-white'
                        : 'text-[#676879] hover:text-[#161616]'
                    }`}
                  >
                    {currentDept.primaryLabel}
                  </button>
                  <button
                    type="button"
                    aria-pressed={showSecondaryView}
                    onClick={() => setShowSecondaryView(true)}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      showSecondaryView
                        ? 'bg-[#0B1120] text-white'
                        : 'text-[#676879] hover:text-[#161616]'
                    }`}
                  >
                    {currentDept.secondaryLabel}
                  </button>
                </div>
              </div>

              <div className="aspect-[16/10] w-full overflow-hidden bg-[#F5F5F2]">
                <img
                  src={showSecondaryView ? currentDept.secondaryImage : currentDept.dashboardImage}
                  alt={showSecondaryView ? currentDept.secondaryAlt : currentDept.dashboardAlt}
                  className="w-full h-full object-cover object-left-top block"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
